import React, { useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Button from "../Button";

import "react-toastify/dist/ReactToastify.css";

import "../../../styles/chat.scss";

import useChat from "../../../hooks/useChat";
import { Form, Formik } from "formik";
import { PAGE_STATE, langComponent } from "../../../constants";

import { ReactComponent as SendSvg } from "../../../assets/icons/send.svg";
import { ReactComponent as InfoSvg } from "../../../assets/icons/chat/info.svg";
import { ReactComponent as MaximizeSvg } from "../../../assets/icons/chat/maximize.svg";
import { ReactComponent as MinimizeSvg } from "../../../assets/icons/chat/minimize.svg";
import { ReactComponent as ReturnSvg } from "../../../assets/icons/chat/return.svg";
import { ReactComponent as AttachmentSvg } from "../../../assets/icons/chat/attach.svg";
import { ReactComponent as ImageSvg } from "../../../assets/icons/chat/image.svg";
import { ReactComponent as ShareLocationSvg } from "../../../assets/icons/chat/map.svg";
import { ReactComponent as TrashSvg } from "../../../assets/icons/trash.svg";
import { ReactComponent as CloseReplySvg } from "../../../assets/chat/close.svg";
import { ReactComponent as PinSvg } from "../../../assets/chat/pin-color666.svg";
import { ReactComponent as ArrowDownSvg } from "../../../assets/chat/arrow-down.svg";

import AppContext from "../../../AppContext";
import CommissionAction from "./CommissionAction";
import CommissionForm from "./CommissionForm";
import Quotation from "./Quotation";
import Invoice from "./Invoice";
import UserMessage from "./UserMessage";
import useCommission from "../../../hooks/useCommissions";
import ChatSessionContext, {
  MESSAGE_TYPE,
  FORM_TYPE,
} from "../../../contexts/ChatSessionContext";
import PaymentForm from "./PaymentForm";
import RateSupport from "./RateSupport";
import SubmitDraft from "./SubmitDraft";
import _ from "lodash";
import CommissionContext from "../../../contexts/CommissionContext";
import Submission from "./Submission";
import { SocketContext } from "../../../contexts/SocketContext.jsx";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode, Scrollbar, Mousewheel } from "swiper/modules";
import { ObjectId } from "bson";
import LocationSharer from "../../LocationSharer/index.jsx";
import { format, getYear, isSameDay } from "date-fns";
import { Impression } from "./Impression.jsx";
import NDASent from "./Tendering/NDASent.jsx";
import NDASigned from "./Tendering/NDASigned.jsx";
import Selected from "./Tendering/Selected.jsx";
import QuotationSent from "./Tendering/QuotationSent.jsx";
import ScheduleSent from "./Tendering/ScheduleSent.jsx";
import Won from "./Tendering/Won.jsx";
import SignContract from "./Tendering/SignContract.jsx";
import ContractSigned from "./Tendering/ContractSigned.jsx";
import WorkSubmitted from "./Tendering/WorkSubmitted.jsx";
import WorkApproved from "./Tendering/WorkApproved.jsx";
const assetsUri = import.meta.env.ASSETS_URI;

const limits = 20;

const Chat = ({ userId, mini, onClose, onReturn, onMaximize }) => {
  const {
    setSessions,
    currentSession,
    getCurrentSession,
    messages,
    setMessages,
    chatMetadata: metadata,
    setChatMetadata: setMetadata,
    setLatestMessage,
    isHiddenMessage,
    pinMessage,
    unpinMessage,
    clearCurrentSession,
    isSupport,
    isMinimizeHidden,
  } = useContext(ChatSessionContext);
  const { socket } = useContext(SocketContext);
  const [pageState, setPageState] = useState(PAGE_STATE.LOADING);
  const inputRef = useRef();
  const contentRef = useRef();
  const commissionRef = useRef(undefined);
  const {
    queryCommissions,
    getCommission,
    querySubmissions,
    getSubmissionFiles,
  } = useCommission();
  const {
    queryChats,
    createChat,
    downloadSessionChats,
    CHAT_TYPE,
    closeSession,
    markRead,
    getFiles,
    deleteChat,
    pinChat,
    unpinChat,
    getChat,
  } = useChat();
  const { user, plans, getLang, viewCompany, viewProfile, shareLocation } =
    useContext(AppContext);

  const [toggleCommissionCTA, setToggleCommissionCTA] = useState(true);
  const [connectivity, setConnectivity] = useState(false);
  const Translation = getLang(langComponent.chat);
  const [imagesToUpload, setImagesToUpload] = useState([]);
  const [filesToUpload, setFilesToUpload] = useState([]);
  const abortSignal = new AbortController();
  const [isReplyMessage, setIsReplyMessage] = useState(undefined);
  const [pins, setPins] = useState([]);
  const [isTogglePins, setIsTogglePins] = useState(false);
  const [supportSessionClosed, setSupportSessionClosed] = useState(false);
  const [isClosingChat, setIsClosingChat] = useState(false);
  const [isShareLocation, setIsShareLocation] = useState(false);

  const scrollWindow = useRef(null);

  const tenderingChatTypes = [
    CHAT_TYPE.TENDERING_NDA_SENT,
    CHAT_TYPE.TENDERING_NDA_SIGNED,
    CHAT_TYPE.TENDERING_SELECTED,
    CHAT_TYPE.TENDERING_QUOTATION_SENT,
    CHAT_TYPE.TENDERING_SCHEDULE_SENT,
    CHAT_TYPE.TENDERING_WON,
    CHAT_TYPE.TENDERING_SIGN_CONTRACT,
    CHAT_TYPE.TENDERING_CONTRACT_SIGNED,
    CHAT_TYPE.TENDERING_WORK_SUBMITTED,
    CHAT_TYPE.TENDERING_WORK_RESUBMITTED,
    CHAT_TYPE.TENDERING_WORK_APPROVED,
  ];

  const { commissions: commission, setCommissions: setCommission } =
    useContext(CommissionContext);

  useEffect(() => {
    if (!currentSession || !socket) return;

    setSupportSessionClosed(!!currentSession.closed);

    setMetadata({});

    init();

    return () => {
      // * Close socket session
      socket.emit(
        "message",
        JSON.stringify({
          event: "sessions:leave",
          to: { type: "session", session: currentSession._id },
        })
      );

      if (currentSession?.endUser?._id) {
        socket.emit(
          "message",
          JSON.stringify({
            event: "user-online:leave",
            to: { users: [currentSession.endUser._id] },
          })
        );
      }

      socket.off("sessions:incoming-msg", handleIncomingMessage);
      socket.off("sessions:msg-unsent", handleMessageUnsent);
      socket.off("sessions:closed", handleSessionClosed);
      socket.off("sessions:msg-pinned", handleMessagePinnedEvent);
      socket.off("sessions:msg-unpinned", handleMessageUnpinnedEvent);
      socket.off("commissions:new", handleNewCommission);
      socket.off("commissions:status-changed", handleCommissionStatusChanged);
      socket.off("commissions:new-submission", handleNewSubmission);
      socket.off("commissions:submission-changed", handleSubmissionChanged);
    };
  }, [currentSession]);

  useEffect(() => {
    // * Mark read unread messages
    const _unreadMessages = messages.filter(
      (c) => !c.readOn && c.createdBy !== user._id && !c.isForm
    );

    if (_unreadMessages.length === 0) return;

    markRead(_unreadMessages);
  }, [messages]);

  const _setCommission = (commission) => {
    setCommission(commission);
    commissionRef.current = commission;
  };

  const handleIncomingMessage = async (msg) => {
    const message = JSON.parse(msg);

    // * Skip if not same session and is not created by the end user
    if (message.session !== currentSession._id) return;

    if (message.type === CHAT_TYPE.MESSAGE && message.createdBy === userId)
      return;

    // * Get attachment or images if present
    if (message.images.length || message.attachments.length) {
      const [file] = await getFiles([message._id]);

      message.images = file.images;
      message.attachments = file.attachments;
    }

    // * Get reply detail if present
    if (message.replied) {
      const _replied = await getChat(message.replied);
      message.replied = _replied;
    }

    setMessages((current) => [message, ...current]);

    // * Mark read the message
    markRead([message._id]);
  };

  const _handleMessageDeleted = (_id) => {
    setMessages((curr) =>
      curr
        .filter((_) => _._id !== _id)
        .map((chat) => {
          if (!chat.replied || chat.replied._id !== _id) return chat;

          chat.replied.active = false;
          chat.replied.deleted = true;

          return chat;
        })
    );

    // * Flag pin deleted
    setPins((curr) =>
      curr.map((pin) => {
        if (pin._id !== _id) return pin;
        pin.deleted = true;
        return pin;
      })
    );
    // document.dispatchEvent(event);
  };

  const handleMessagePinnedEvent = async (msg) => {
    if (!msg) return;

    const chat = JSON.parse(msg);

    setPins((curr) => {
      if (curr.find((pin) => pin._id === chat._id)) {
        return curr;
      }

      return [...curr, chat];
    });
  };

  const handleMessageUnpinnedEvent = (msg) => {
    if (!msg) return;

    const { _id } = JSON.parse(msg);

    setPins((curr) => curr.filter((c) => c._id !== _id));
  };

  const handleMessageUnsent = async (msg) => {
    if (!msg) return;

    const { _id } = JSON.parse(msg);

    if (!_id) return;

    _handleMessageDeleted(_id);
  };

  const handleSessionClosed = async (msg) => {
    const message = JSON.parse(msg);

    // * Skip if not same session and is not created by the end user
    if (message.session !== currentSession._id) return;

    // * Skip if user is staff
    if (getCurrentSession()?.isStaff) return;

    setSupportSessionClosed(true);

    // * Add rating form
    setMessages((current) => [
      {
        isForm: true,
        formType: FORM_TYPE.RATE_SUPPORT,
      },
      ...current,
    ]);
  };

  const handleNewCommission = async (body) => {
    const socketCommission = JSON.parse(body);
    getAndSetCommission({ id: socketCommission.id });
  };

  const handleCommissionStatusChanged = async (body) => {
    const socketCommission = JSON.parse(body);
    if (socketCommission.id !== commissionRef.current._id) return;
    getAndSetCommission({ id: commissionRef.current._id });
  };

  const handleNewSubmission = async (body) => {
    const socketBody = JSON.parse(body);

    console.log(socketBody);
  };

  const handleSubmissionChanged = async (body) => {
    const socketBody = JSON.parse(body);
    console.log(socketBody);
  };

  const getAndSetCommission = async ({ id, commission }) => {
    let _commission = _.cloneDeep(commission);

    if (!_commission) {
      _commission = await getCommission(id);
    }

    // * Get submissions
    const { data: _submissions } = await querySubmissions(_commission._id);
    _commission.submissions = _submissions;

    // * Get files
    const promiseResults = await Promise.allSettled(
      _submissions.map((s) => getSubmissionFiles(_commission._id, s._id))
    );

    for (let i = 0; i < _submissions.length; i++) {
      if (promiseResults[i].status === "rejected") {
        _submissions[i].files = [];
        continue;
      }

      _submissions[i].files = promiseResults[i].value;
    }

    // * Check if you are owner
    _commission.isOwner = user._id === _commission.createdBy;

    _setCommission(_commission);
  };

  const handleUserConnectivity = async (body) => {
    const { userId, online } = JSON.parse(body);

    setSessions((curr) => {
      return curr.map((_) => {
        if (_.endUser._id !== userId) {
          return _;
        }

        _.endUser.connectivity.online = online;
        return _;
      });
    });
  };

  const init = async () => {
    // if (!currentSession.support) {
    //   let { data: commissions } = await queryCommissions(
    //     {
    //       status: { $nin: [4, 5] },
    //       createdBy: { $in: [user._id, currentSession.endUser._id] },
    //       commissionedBy: { $in: [user._id, currentSession.endUser._id] },
    //       active: true,
    //       deleted: false,
    //     },
    //     1,
    //     1,
    //     null,
    //     {
    //       createdOn: -1,
    //     }
    //   );

    //   if (Array.isArray(commissions) && commissions.length === 1) {
    //     getAndSetCommission({ commission: commissions[0] });
    //   }
    // }

    setConnectivity(currentSession.endUser.connectivity?.online);

    // * Init socket session
    socket.emit(
      "message",
      JSON.stringify({
        event: "sessions:join",
        to: { type: "session", session: currentSession._id },
      })
    );

    socket.emit(
      "message",
      JSON.stringify({
        event: "user-online:join",
        to: { users: [currentSession.endUser._id] },
      })
    );

    socket.on("sessions:incoming-msg", handleIncomingMessage);
    socket.on("sessions:msg-unsent", handleMessageUnsent);
    socket.on("sessions:closed", handleSessionClosed);
    socket.on("sessions:msg-pinned", handleMessagePinnedEvent);
    socket.on("sessions:msg-unpinned", handleMessageUnpinnedEvent);
    socket.on("commissions:new", handleNewCommission);
    socket.on("commissions:status-changed", handleCommissionStatusChanged);
    socket.on("commissions:new-submission", handleNewSubmission);
    socket.on("commissions:submission-changed", handleSubmissionChanged);

    fetchChats({
      currentPage: 1,
    })
      .then(async (chats) => {
        // * Fetch images or attachments if present
        const chatIdsWithFiles = chats
          .filter(
            (_) =>
              (!!_.images && _.images.length > 0) ||
              (!!_.attachments && _.attachments.length > 0)
          )
          .map((_) => _._id);

        const chatReplyIds = chats
          .filter((_) => !!_.replied)
          .map((c) => c.replied);

        if (chatIdsWithFiles.length === 0 && chatReplyIds.length === 0) {
          setMessages(chats);
          setPageState(PAGE_STATE.IDLE);
          return;
        }

        let _repliedChats = [];

        if (chatReplyIds.length > 0) {
          const { data: _data } = await queryChats(
            { _id: { $in: chatReplyIds } },
            1,
            _repliedChats.length
          );

          _repliedChats = _data;
        }

        const _chatFiles =
          chatIdsWithFiles.length > 0 ? await getFiles(chatIdsWithFiles) : [];

        const _chats = chats.map((c) => {
          if (
            (!c.images || c.images.length === 0) &&
            (!c.attachments || c.attachments.length === 0) &&
            !c.replied
          )
            return c;

          const _matchFile = _chatFiles.find((f) => f._id === c._id);
          const _repliedToChat = _repliedChats.find((r) => r._id === c.replied);

          if (!_matchFile && !_repliedToChat) return c;

          c.images = _matchFile?.images ?? [];
          c.attachments = _matchFile?.attachments ?? [];
          c.replied = _repliedToChat;

          return c;
        });

        setMessages(_chats);
        setPageState(PAGE_STATE.IDLE);
      })
      .catch((err) => {
        console.error(err);
      });

    // * Get pins
    if (currentSession.pins.length > 0) {
      const { data: _pins } = await queryChats(
        {
          _id: { $in: currentSession.pins },
        },
        null,
        currentSession.pins.length
      );

      setPins(
        currentSession.pins
          .map((p) => {
            const _match = _pins.find((_) => _._id === p);

            return _match;
          })
          .filter((_) => !!_)
      );
    }

    // inputRef.current?.focus();
  };

  const fetchChats = async (newMetadata, signaller) => {
    let _filter = { session: currentSession._id, deleted: false };

    if (currentSession.hideSince) {
      _filter["createdOn"] = {
        $gt: new Date(currentSession.hideSince),
      };
    }

    const { data, _metadata } = await queryChats(
      _filter,
      newMetadata?.currentPage || metadata?.currentPage + 1 || 1,
      limits,
      { createdOn: -1 },
      signaller
    );

    setMetadata(_metadata);

    return data;
  };

  const handleComissionActionClick = (action) => {
    setToggleCommissionCTA(false);

    switch (action) {
      case "quotation":
        setMessages((current) => [
          ...current,
          {
            isForm: true,
            formType: FORM_TYPE.CREATE_QUOTATION,
          },
        ]);
        break;

      case "pay":
        setMessages((current) => [
          ...current,
          {
            isForm: true,
            formType: FORM_TYPE.PAY,
          },
        ]);
        break;
      case "submit-draft":
        setMessages((current) => [
          ...current,
          {
            isForm: true,
            formType: FORM_TYPE.SUBMIT_DRAFT,
          },
        ]);
        break;
      default:
        break;
    }
  };

  const handleDownloadChatClick = async () => {
    const data = await downloadSessionChats(currentSession._id);
    const a = document.createElement("a");
    a.href = `data:text/plain;base64,${data}`;
    a.download = "chat.txt";
    a.click();
  };

  const handleEndChatClick = async () => {
    try {
      setIsClosingChat(true);
      await closeSession(currentSession._id);
      onReturn();
    } catch (error) {
    } finally {
      setIsClosingChat(true);
    }
  };

  const handleSubmit = async ({ msg }, { resetForm }) => {
    if (!msg && imagesToUpload.length === 0 && filesToUpload.length === 0)
      return;

    let _fileType = undefined;
    let _files = [];

    // * Create images chat
    if (imagesToUpload.length > 0) {
      const _id = new ObjectId().toString();

      setMessages((current) => [
        {
          _id,
          type: MESSAGE_TYPE.MESSAGE,
          images: imagesToUpload.map((_) => ({ file: URL.createObjectURL(_) })),
          createdBy: user._id,
          replied: isReplyMessage,
        },
        ...current,
      ]);

      createChat(currentSession._id, CHAT_TYPE.MESSAGE, {
        _id: _id,
        fileType: "image",
        files: imagesToUpload,
      })
        .then((newChat) => {
          setMessages((current) =>
            current.map((message) => {
              if (message._id !== newChat._id) return message;

              message.createdOn = newChat.createdOn;
              return message;
            })
          );
        })
        .catch((err) => console.error(err));
    }

    if (filesToUpload.length > 0) {
      const _id = new ObjectId().toString();

      setMessages((current) => [
        {
          _id,
          type: MESSAGE_TYPE.MESSAGE,
          attachments: filesToUpload.map((_) => ({
            name: _.name,
            size: _.size,
            file: URL.createObjectURL(_),
          })),
          createdBy: user._id,
          replied: isReplyMessage,
        },
        ...current,
      ]);

      createChat(currentSession._id, CHAT_TYPE.MESSAGE, {
        _id: _id,
        fileType: "attachment",
        files: filesToUpload,
      })
        .then((newChat) => {
          setMessages((current) =>
            current.map((message) => {
              if (message._id !== newChat._id) return message;

              message.createdOn = newChat.createdOn;
              return message;
            })
          );
        })
        .catch((err) => console.error(err));
    }

    if (msg) {
      const _id = new ObjectId().toString();

      setMessages((current) => [
        {
          _id,
          msg,
          type: MESSAGE_TYPE.MESSAGE,
          createdBy: user._id,
          replied: isReplyMessage,
        },
        ...current,
      ]);

      createChat(currentSession._id, CHAT_TYPE.MESSAGE, {
        _id: _id,
        msg,
        replied: isReplyMessage?._id,
      })
        .then((newChat) => {
          setMessages((current) =>
            current.map((message) => {
              if (message._id !== newChat._id) return message;

              message.createdOn = newChat.createdOn;
              return message;
            })
          );
        })
        .catch((err) => console.error(err));
    }

    setLatestMessage({
      session: currentSession._id,
      msg,
      attachments: filesToUpload,
      images: imagesToUpload,
      createdBy: user._id,
      createdOn: new Date(),
    });
    resetForm();
    setImagesToUpload([]);
    setFilesToUpload([]);
    setIsReplyMessage(undefined);
    const container = document.querySelector(".chat-content");
    container.scrollTop = container.scrollHeight;
  };

  const getCTAs = () => {
    const { support, closed } = currentSession;

    // * Only has ctas for support
    if (!support) {
      return <></>;
    }

    if (!closed && currentSession.isStaff) {
      return (
        <div className="chat-header-ctas">
          {/* <Button label={Translation("Request remote")} variant={"secondary"} /> */}
          <Button
            label={Translation("End chat")}
            onClick={handleEndChatClick}
            submitting={isClosingChat}
          />
        </div>
      );
    }

    if (closed && currentSession.isStaff) {
      return (
        <div className="chat-header-ctas">
          <Button
            label={Translation("Download Log")}
            onClick={handleDownloadChatClick}
          />
        </div>
      );
    }

    return <></>;
  };

  /**
   * Display status of current commission in header
   */
  const getCurrentCommissionStatus = () => {
    if (!commission) return null;

    const { support } = currentSession;

    // * Skip if support session
    if (support) return null;

    let classNameAttribute = "";
    let status = "";

    if (commission.status === 0) {
      classNameAttribute = "quotation";
      status = "Waiting confirmation";
    }

    if (commission.status === 1) {
      classNameAttribute = "waiting-payment";
      status = "Waiting payment";
    }

    if (commission.status === 2) {
      classNameAttribute = "in-progress";
      status = commission.isOwner ? "Waiting your draft" : "Working";
    }

    if (commission.status === 3) {
      classNameAttribute = "in-progress";
      status = commission.isOwner ? "Reviewing draft" : "Draft submitted";
    }

    return (
      <span
        className={`chat-header-info-info__commission-status chat-header-info-info__commission-status--${classNameAttribute}`}
      >
        <div className="chat-header-info-info__commission-status__dot" />
        {Translation(status)}
      </span>
    );
  };

  const handleScroll = (e) => {
    if (
      e.currentTarget.scrollTop * -1 + e.currentTarget.clientHeight <
      e.currentTarget.scrollHeight
    ) {
      return;
    }

    fetchChats().then(async (chats) => {
      if (chats.length === 0) return;

      // * Fetch images or attachments if present
      const chatIdsWithFiles = chats
        .filter(
          (_) =>
            (!!_.images && _.images.length > 0) ||
            (!!_.attachments && _.attachments.length > 0)
        )
        .map((_) => _._id);

      if (chatIdsWithFiles.length === 0) {
        setMessages((current) => [...current, ...chats.reverse()]);
        return;
      }

      const chatFiles = await getFiles(chatIdsWithFiles);

      const _chats = chats.map((c) => {
        if (
          (!c.images || c.images.length === 0) &&
          (!c.attachments || c.attachments.length === 0)
        )
          return c;

        const _match = chatFiles.find((_) => _._id === c._id);

        if (!_match) return c;

        c.images = _match.images ?? [];
        c.attachments = _match.attachments ?? [];
        return c;
      });

      setMessages((current) => [...current, ..._chats.reverse()]);
    });
  };

  const handleSubmitDraftSuccess = () => {
    setCommission({
      ...commission,
      status: 3,
    });
  };

  const _getAvatarSource = () => {
    if (!currentSession.endUser.isCompany) {
      return currentSession.endUser.avatar;
    }

    return currentSession.endUser.logo;
  };

  const _getEndUsersName = (firstnameOnly) => {
    if (!currentSession.endUser.isCompany) {
      return `${currentSession.endUser.firstname} ${
        !firstnameOnly
          ? currentSession.endUser.surname ||
            currentSession.endUser.lastname ||
            ""
          : ""
      }`;
    }

    return currentSession.endUser.name;
  };

  const _getPlan = () => {
    if (currentSession.support && !currentSession.isStaff) {
      return "";
    }

    if (currentSession.endUser.isCompany && currentSession.endUser.enterprise) {
      return "Enterprise ";
    }

    if (
      currentSession.endUser.isCompany &&
      !currentSession.endUser.enterprise
    ) {
      return "Business ";
    }

    return `${plans.find((p) => currentSession.endUser.plan).name} `;
  };

  const _handleImageUpload = (files) => {
    setImagesToUpload([...files]);
    inputRef.current?.focus();
  };

  const _handleFileUpload = (files) => {
    setFilesToUpload([...files]);
    inputRef.current?.focus();
  };

  const _handleRemoveImageToUploadClick = (index) => {
    const _images = _.cloneDeep(imagesToUpload);
    _images.splice(index, 1);
    setImagesToUpload(_images);
  };

  const _handleRemoveFileToUploadClick = (index) => {
    const _files = _.cloneDeep(filesToUpload);
    _files.splice(index, 1);
    setFilesToUpload(_files);
  };

  const handleViewProfileClick = () => {
    if (isSupport) return;
    if (user?.support) return;

    if (currentSession.endUser.isCompany) {
      viewCompany(currentSession.endUser._id);
      onClose();
      return;
    }

    viewProfile(currentSession.endUser._id);
    onClose();
  };

  const _handleReplyMessageClick = (message) => {
    setIsTogglePins(false);
    setIsReplyMessage(message);
    inputRef.current?.focus();
  };

  const _handleScrollToReply = async (replyChatId) => {
    const element = document.getElementById(`chat-${replyChatId}`);

    // * If message not found then fetch up to that message first
    if (!element) {
      return;
    }

    element.scrollIntoView({ block: "center" });

    element.classList.add("message-shake");

    // Remove the class after the animation completes so it can be re-applied next time
    setTimeout(function () {
      element.classList.remove("message-shake");
    }, 500); // Match the duration of the animation
  };

  const _handlePinMessage = async (message) => {
    await pinChat(message._id);
    setPins((curr) => {
      if (curr.find((pin) => pin._id === message._id)) {
        return curr;
      }

      return [...curr, message];
    });
    pinMessage(message._id);
  };

  const _handleUnpinMessage = async (message) => {
    await unpinChat(message._id);
    setPins((curr) => curr.filter((c) => c._id !== message._id));
    unpinMessage(message._id);
  };

  const _getPinRender = () => {
    const _pins = _.cloneDeep(pins)
      ?.filter((p) => !isHiddenMessage(p.createdOn))
      .reverse();

    if (!_pins || _pins.length === 0) return null;

    const _classNames = ["chat-pins"];

    if (_pins.length > 1) {
      _classNames.push("chat-pins--has-more");
    }

    if (isTogglePins) {
      _classNames.push("chat-pins--toggled");
    }

    function _getMessage(_pin, index) {
      const _msgClassnames = ["chat-pins-msg"];

      let _content = _pin.msg ?? "";

      if (_pin.attachments?.length > 0) {
        _msgClassnames.push("chat-pins-msg--italic");

        _content = "Attachment";
      }

      if (_pin.images?.length > 0) {
        _msgClassnames.push("chat-pins-msg--italic");

        _content = "Image";
      }

      if (_pin.deleted) {
        _msgClassnames.push("chat-pins-msg--italic");

        _content = "Message unavailable";
      }

      return (
        <div
          className={_msgClassnames.join(" ")}
          key={`pin-${index}`}
          onClick={() => {
            _handleScrollToReply(_pin._id);
            setIsTogglePins(false);
          }}
        >
          {index === 0 && (
            <PinSvg width={16} className="chat-pins-msg__header" />
          )}
          <div className="chat-pins-msg__text">{_content}</div>
          <div
            className="chat-pins-msg__unpin"
            onClick={(e) => {
              e.stopPropagation();
              _handleUnpinMessage(_pin);
            }}
          >
            Unpin
          </div>
          {index === 0 && pins.length > 1 && (
            <ArrowDownSvg
              className={`chat-pins-msg__toggle chat-pins-msg__toggle--${
                isTogglePins ? "active" : "inactive"
              }`}
              width={17}
              style={{ margin: "-12px 0" }}
              onClick={(e) => {
                e.stopPropagation();
                if (index > 0) return;
                if (pins.length === 1) return;

                setIsTogglePins((curr) => !curr);
              }}
            />
          )}
        </div>
      );
    }

    return (
      <div className={_classNames.join(" ")}>
        {_pins.map((p, index) => _getMessage(p, index))}
      </div>
    );
  };

  const handleReturnClick = () => {
    clearCurrentSession();
    setPins([]);
    onReturn();
  };

  const handleShareLocationClick = () => {
    shareLocation(
      () => {},
      (detail) => {
        const {
          lat,
          lng,
          address: { name, address },
        } = detail;
        const _id = new ObjectId().toString();

        setMessages((current) => [
          {
            _id,
            type: MESSAGE_TYPE.MESSAGE,
            location: {
              lat,
              lng,
              name,
              address,
            },
            createdBy: user._id,
          },
          ...current,
        ]);

        createChat(currentSession._id, CHAT_TYPE.MESSAGE, {
          _id: _id,
          location: {
            lat,
            lng,
            name,
            address,
          },
        })
          .then((newChat) => {
            setMessages((current) =>
              current.map((message) => {
                if (message._id !== newChat._id) return message;

                message.createdOn = newChat.createdOn;
                return message;
              })
            );
          })
          .catch((err) => console.error(err));
      }
    );
  };

  const _displayDateSeparator = (currentIndex, messageDate) => {
    const _messageDate = messageDate ? new Date(messageDate) : new Date();
    const _isSameYear = getYear(_messageDate) === getYear(new Date());

    const toDisplay = (
      <span className="chat-content__date-separator">
        {format(_messageDate, `MMM d${_isSameYear ? " (eee)" : ", yyyy"}`)}
      </span>
    );

    if (currentIndex + 1 === messages.length) return toDisplay;

    const isSameAsPrev = isSameDay(
      _messageDate,
      new Date(messages[currentIndex + 1].createdOn)
    );

    if (!isSameAsPrev) {
      return toDisplay;
    }

    return <></>;
  };

  if (pageState === PAGE_STATE.LOADING) {
    return <></>;
  }

  const avatar = _getAvatarSource();

  return (
    <>
      <div className="chat">
        <div className={`chat-header${mini ? " chat-header--mini" : ""}`}>
          {mini && !currentSession.support && (
            <ReturnSvg
              className="chat-header-return"
              onClick={handleReturnClick}
            />
          )}
          <div className="chat-header-info">
            <img
              className="chat-header-info__avatar"
              src={`${assetsUri}/${avatar}`}
              onClick={handleViewProfileClick}
              onError={({ target }) => {
                target.src = avatar;
              }}
            />
            <span
              className="chat-header-info__name"
              onClick={handleViewProfileClick}
            >
              {_getEndUsersName()}
            </span>
            <span className="chat-header-info-info">
              <span className="chat-header-info-info__plan">
                {_getPlan()}
                {connectivity ? Translation("Online") : Translation("Offline")}
              </span>
              {getCurrentCommissionStatus()}
            </span>
          </div>
          {getCTAs()}
          {mini && (
            <div className="chat-header-mini">
              {!isSupport && (
                <div
                  className="chat-header-mini-option chat-header-mini-option__maximize"
                  onClick={onMaximize}
                >
                  <MaximizeSvg className="chat-header-mini-option-icon" />
                </div>
              )}
              {!isMinimizeHidden && (
                <div
                  className="chat-header-mini-option chat-header-mini-option__minimize"
                  onClick={onClose}
                >
                  <MinimizeSvg className="chat-header-mini-option-icon" />
                </div>
              )}
            </div>
          )}
        </div>
        <div className="chat-scroller" ref={scrollWindow}>
          {_getPinRender()}
          <div
            className="chat-content"
            onScroll={handleScroll}
            ref={contentRef}
          >
            {messages.map(
              (
                {
                  isForm,
                  type,
                  formType,
                  quotation: quotationId,
                  invoice: invoiceId,
                  draft,
                  readOn,
                  readBy,
                  work,
                  ...message
                },
                msgIndex
              ) => {
                // * Display message depending on type
                if (isForm && formType === FORM_TYPE.CREATE_QUOTATION) {
                  return (
                    <CommissionForm
                      key={`msg-${msgIndex}`}
                      onClose={() => setToggleCommissionCTA(true)}
                    />
                  );
                }

                if (isForm && formType === FORM_TYPE.PAY) {
                  return (
                    <PaymentForm
                      commission={commission}
                      key={`msg-${msgIndex}`}
                      onClose={() => setToggleCommissionCTA(true)}
                    />
                  );
                }

                if (isForm && formType === FORM_TYPE.RATE_SUPPORT) {
                  return <RateSupport onClose={onClose} />;
                }

                if (isForm && formType === FORM_TYPE.SUBMIT_DRAFT) {
                  return (
                    <SubmitDraft
                      chatSessionId={currentSession._id}
                      commission={commission}
                      key={`msg-${msgIndex}`}
                      onClose={() => setToggleCommissionCTA(true)}
                      onSuccess={handleSubmitDraftSuccess}
                    />
                  );
                }

                // * ============ Tendering ============
                if (tenderingChatTypes.includes(type)) {
                  if (type === CHAT_TYPE.TENDERING_NDA_SIGNED) {
                    return <NDASigned />;
                  }

                  if (type === CHAT_TYPE.TENDERING_CONTRACT_SIGNED) {
                    return <ContractSigned tenderingId={message.tendering} />;
                  }

                  if (message.createdBy === user._id) {
                    return <></>;
                  }

                  if (type === CHAT_TYPE.TENDERING_NDA_SENT) {
                    return <NDASent tenderingId={message.tendering} />;
                  }

                  if (type === CHAT_TYPE.TENDERING_SELECTED) {
                    return <Selected tenderingId={message.tendering} />;
                  }

                  if (type === CHAT_TYPE.TENDERING_QUOTATION_SENT) {
                    return <QuotationSent quotationId={quotationId} />;
                  }

                  if (type === CHAT_TYPE.TENDERING_SCHEDULE_SENT) {
                    return <ScheduleSent quotationId={quotationId} />;
                  }

                  if (type === CHAT_TYPE.TENDERING_WON) {
                    return <Won />;
                  }

                  if (type === CHAT_TYPE.TENDERING_SIGN_CONTRACT) {
                    return <SignContract tenderingId={message.tendering} />;
                  }

                  if (
                    type === CHAT_TYPE.TENDERING_WORK_SUBMITTED ||
                    type === CHAT_TYPE.TENDERING_WORK_RESUBMITTED
                  ) {
                    return (
                      <WorkSubmitted
                        tenderingId={message.tendering}
                        submission={message.submission}
                        resubmitted={
                          type === CHAT_TYPE.TENDERING_WORK_RESUBMITTED
                        }
                      />
                    );
                  }

                  if (type === CHAT_TYPE.TENDERING_WORK_APPROVED) {
                    return (
                      <WorkApproved
                        tenderingId={message.tendering}
                        submission={message.submission}
                      />
                    );
                  }
                }
                // * ===================================

                if (type === CHAT_TYPE.INVOICE) {
                  return (
                    <Invoice
                      key={`msg-${msgIndex}`}
                      id={invoiceId}
                      onPay={() => handleComissionActionClick("pay")}
                    />
                  );
                }

                if (type === CHAT_TYPE.SUBMISSION) {
                  return (
                    <Submission
                      key={`msg-${msgIndex}`}
                      commissionId={draft.commission}
                      submissionId={draft.submission}
                    />
                  );
                }

                if (type === CHAT_TYPE.IMPRESSION) {
                  return (
                    <Impression
                      workId={work}
                      isOwner={message.createdBy !== user._id}
                    />
                  );
                }

                if (
                  type === CHAT_TYPE.IMPRESSION &&
                  message.createdBy === user._id
                ) {
                  return <></>;
                }

                const isFirstMessage = msgIndex === 0;
                let lastMessage = messages[msgIndex - 1];

                if (isFirstMessage || lastMessage.type !== CHAT_TYPE.MESSAGE) {
                  lastMessage = null;
                }

                // * Check if previos message is same date or not

                return (
                  <>
                    <UserMessage
                      key={`msg-${msgIndex}`}
                      userId={userId}
                      message={message}
                      isFirstMessage={isFirstMessage}
                      lastMessage={lastMessage}
                      read={{ on: readOn, by: readBy }}
                      onDelete={() => _handleMessageDeleted(message._id)}
                      onReply={() => _handleReplyMessageClick(message)}
                      onScrollToReply={() => {
                        // * Do nothing if message is older than hideSince
                        if (isHiddenMessage(message.replied.createdOn)) return;

                        _handleScrollToReply(message.replied._id);
                      }}
                      onPin={() => _handlePinMessage(message)}
                      onUnpin={() => _handleUnpinMessage(message)}
                    />
                    {_displayDateSeparator(msgIndex, message.createdOn)}
                  </>
                );
              }
            )}
          </div>
        </div>
        {!supportSessionClosed && (
          <div className="chat-input">
            {imagesToUpload.length > 0 && (
              <Swiper
                className="chat-input-image-gallery"
                direction={"horizontal"}
                freeMode={true}
                // scrollbar={true}
                mousewheel={true}
                modules={[FreeMode, Mousewheel]}
                // loop={true}
                slidesPerView={"auto"}
                spaceBetween={12}
              >
                {[...imagesToUpload].map((image, index) => {
                  const _src = URL.createObjectURL(image);

                  return (
                    <SwiperSlide
                      key={index}
                      className="chat-input-image-gallery-image"
                    >
                      <img src={_src} alt="image" height={60} />
                      <TrashSvg
                        className="chat-input-image-gallery-image__trash"
                        onClick={() => _handleRemoveImageToUploadClick(index)}
                      />
                    </SwiperSlide>
                  );
                })}
              </Swiper>
            )}
            {filesToUpload.length > 0 && (
              <div className="chat-input-file-list">
                {[...filesToUpload].map((file, index) => {
                  // const _src = URL.createObjectURL(image);

                  return (
                    <div className="chat-input-file-list-item">
                      <span className="chat-input-file-list-item__name">
                        {file.name}
                      </span>
                      <TrashSvg
                        className="chat-input-file-list-item__trash"
                        onClick={() => _handleRemoveFileToUploadClick(index)}
                      />
                    </div>
                  );
                })}
              </div>
            )}
            {isReplyMessage && (
              <div className="chat-input-replying">
                <span className="chat-input-replying__to">
                  Replying to{" "}
                  {isReplyMessage.createdBy === user._id
                    ? "yourself"
                    : _getEndUsersName(true)}
                </span>
                <span className="chat-input-replying__msg">
                  {isReplyMessage.msg ? isReplyMessage.msg : "Attachment"}
                </span>
                <div
                  className="chat-input-replying__close"
                  onClick={() => setIsReplyMessage(undefined)}
                >
                  <CloseReplySvg width={16} />
                </div>
              </div>
            )}
            <div className="chat-input-actions">
              <label
                className="chat-input-actions-action"
                htmlFor="chat__file-uploader"
              >
                <AttachmentSvg width={20} />
              </label>
              <label
                className="chat-input-actions-action"
                htmlFor="chat__image-uploader"
              >
                <ImageSvg width={18} />
              </label>
              <label
                className="chat-input-actions-action"
                onClick={handleShareLocationClick}
              >
                <ShareLocationSvg width={18} />
              </label>
            </div>
            <Formik initialValues={{ msg: "" }} onSubmit={handleSubmit}>
              {({ values, setFieldValue, submitForm }) => {
                const handleInputChanged = ({ target: { value } }) => {
                  setFieldValue("msg", value);
                };

                return (
                  <Form className="chat-input-textbox">
                    <input
                      ref={inputRef}
                      className="chat-input-textbox__input"
                      placeholder={Translation("Type a message")}
                      value={values.msg}
                      onChange={handleInputChanged}
                      autoFocus
                    />
                    <SendSvg
                      className="chat-input-textbox__send-button"
                      onClick={submitForm}
                    />
                  </Form>
                );
              }}
            </Formik>
          </div>
        )}
        <input
          id="chat__image-uploader"
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => _handleImageUpload(e.target.files)}
        />
        <input
          id="chat__file-uploader"
          type="file"
          accept="application/pdf, application/msword, application/vnd.ms-excel, text/plain, application/zip"
          multiple
          onChange={(e) => _handleFileUpload(e.target.files)}
        />
      </div>
    </>
  );
};

Chat.propTypes = {
  userId: PropTypes.string.isRequired,
  mini: PropTypes.bool,
  onReturn: PropTypes.func,
};

export default Chat;
